import type { ReactNode } from "react";
import { Box } from "@mui/material";
import { normalizeChartTypeId } from "../../utils/chartTypeRegistry";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

type Props = {
  typeId: string;
  size?: number;
};

const ACCENT = "#1A73E8";
const MUTED = "#90A4AE";

function SvgWrap({ size, children }: { size: number; children: ReactNode }) {
  const custom = useAppCustomStyles();
  return (
    <Box
      component="svg"
      viewBox="0 0 32 20"
      width={size}
      height={(size * 20) / 32}
      sx={custom.components.chartTypeIcon.svgWrap}
      aria-hidden
    >
      {children}
    </Box>
  );
}

function BarIcon({ size }: { size: number }) {
  return (
    <SvgWrap size={size}>
      <rect x="4" y="10" width="4" height="8" fill={ACCENT} rx="0.5" />
      <rect x="11" y="6" width="4" height="12" fill={ACCENT} rx="0.5" />
      <rect x="18" y="3" width="4" height="15" fill={ACCENT} rx="0.5" />
      <rect x="25" y="8" width="4" height="10" fill={ACCENT} rx="0.5" />
    </SvgWrap>
  );
}

function LineIcon({ size, area }: { size: number; area?: boolean }) {
  return (
    <SvgWrap size={size}>
      {area ? <polygon points="3,17 8,12 14,14 20,6 29,10 29,17 3,17" fill={`${ACCENT}33`} /> : null}
      <polyline
        points="3,17 8,12 14,14 20,6 29,10"
        fill="none"
        stroke={ACCENT}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </SvgWrap>
  );
}

function PieIcon({ size, doughnut, rose }: { size: number; doughnut?: boolean; rose?: boolean }) {
  const r = doughnut ? 7 : 8;
  const cx = 16;
  const cy = 10;
  return (
    <SvgWrap size={size}>
      {doughnut ? <circle cx={cx} cy={cy} r={r + 2} fill="none" stroke={MUTED} strokeWidth="3" /> : null}
      <path d={`M ${cx} ${cy} L ${cx} ${cy - r} A ${r} ${r} 0 0 1 ${cx + r * 0.9} ${cy + r * 0.4} Z`} fill={ACCENT} />
      <path d={`M ${cx} ${cy} L ${cx + r * 0.9} ${cy + r * 0.4} A ${r} ${r} 0 0 1 ${cx - r * 0.5} ${cy + r * 0.8} Z`} fill="#4FC3F7" />
      <path d={`M ${cx} ${cy} L ${cx - r * 0.5} ${cy + r * 0.8} A ${r} ${r} 0 0 1 ${cx} ${cy - r} Z`} fill="#81C784" />
      {rose ? <circle cx={cx} cy={cy} r="1.5" fill="#fff" /> : null}
    </SvgWrap>
  );
}

function ScatterIcon({ size, effect }: { size: number; effect?: boolean }) {
  const color = effect ? "#E91E63" : ACCENT;
  return (
    <SvgWrap size={size}>
      {effect ? <circle cx="8" cy="12" r="5" fill={`${color}22`} /> : null}
      <circle cx="6" cy="14" r="1.8" fill={color} />
      <circle cx="12" cy="8" r="1.8" fill={color} />
      <circle cx="18" cy="13" r="1.8" fill={color} />
      <circle cx="24" cy="6" r="1.8" fill={color} />
      <circle cx="27" cy="15" r="1.8" fill={color} />
    </SvgWrap>
  );
}

function FunnelIcon({ size }: { size: number }) {
  return (
    <SvgWrap size={size}>
      <polygon points="6,3 26,3 22,8 10,8" fill={ACCENT} />
      <polygon points="10,9 22,9 19,14 13,14" fill="#4FC3F7" />
      <polygon points="13,15 19,15 17,18 15,18" fill="#81C784" />
    </SvgWrap>
  );
}

function RadarIcon({ size }: { size: number }) {
  return (
    <SvgWrap size={size}>
      <polygon points="16,3 26,9 22,18 10,18 6,9" fill="none" stroke={MUTED} strokeWidth="0.8" />
      <polygon points="16,6 22,10 20,15 12,15 10,10" fill={`${ACCENT}44`} stroke={ACCENT} strokeWidth="1" />
    </SvgWrap>
  );
}

function GaugeIcon({ size }: { size: number }) {
  return (
    <SvgWrap size={size}>
      <path d="M 6 14 A 10 10 0 0 1 26 14" fill="none" stroke={MUTED} strokeWidth="2" />
      <path d="M 6 14 A 10 10 0 0 1 22 7" fill="none" stroke={ACCENT} strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="16" cy="14" r="1.5" fill={ACCENT} />
    </SvgWrap>
  );
}

function HierarchyIcon({ size, kind }: { size: number; kind: "sunburst" | "treemap" | "tree" }) {
  if (kind === "treemap") {
    return (
      <SvgWrap size={size}>
        <rect x="3" y="3" width="13" height="14" fill={ACCENT} rx="0.5" />
        <rect x="17" y="3" width="12" height="7" fill="#4FC3F7" rx="0.5" />
        <rect x="17" y="11" width="12" height="6" fill="#81C784" rx="0.5" />
      </SvgWrap>
    );
  }
  if (kind === "tree") {
    return (
      <SvgWrap size={size}>
        <circle cx="16" cy="4" r="2" fill={ACCENT} />
        <line x1="16" y1="6" x2="10" y2="11" stroke={MUTED} strokeWidth="1" />
        <line x1="16" y1="6" x2="22" y2="11" stroke={MUTED} strokeWidth="1" />
        <circle cx="10" cy="12" r="1.8" fill="#4FC3F7" />
        <circle cx="22" cy="12" r="1.8" fill="#4FC3F7" />
        <line x1="10" y1="14" x2="7" y2="17" stroke={MUTED} strokeWidth="1" />
        <line x1="22" y1="14" x2="25" y2="17" stroke={MUTED} strokeWidth="1" />
        <circle cx="7" cy="18" r="1.5" fill="#81C784" />
        <circle cx="25" cy="18" r="1.5" fill="#81C784" />
      </SvgWrap>
    );
  }
  return (
    <SvgWrap size={size}>
      <circle cx="16" cy="10" r="3" fill={ACCENT} />
      <circle cx="10" cy="14" r="2" fill="#4FC3F7" />
      <circle cx="22" cy="14" r="2" fill="#4FC3F7" />
      <circle cx="7" cy="17" r="1.5" fill="#81C784" />
      <circle cx="13" cy="17" r="1.5" fill="#81C784" />
      <circle cx="19" cy="17" r="1.5" fill="#81C784" />
      <circle cx="25" cy="17" r="1.5" fill="#81C784" />
    </SvgWrap>
  );
}

function HeatmapIcon({ size, calendar }: { size: number; calendar?: boolean }) {
  const colors = [ACCENT, "#4FC3F7", "#81C784", "#FFB74D", ACCENT, MUTED, "#4FC3F7", ACCENT];
  return (
    <SvgWrap size={size}>
      {calendar ? (
        <>
          <rect x="4" y="2" width="24" height="16" fill="none" stroke={MUTED} strokeWidth="0.8" rx="1" />
          {Array.from({ length: 12 }).map((_, i) => (
            <rect
              key={i}
              x={5 + (i % 4) * 5.5}
              y={5 + Math.floor(i / 4) * 4}
              width="4.5"
              height="3"
              fill={colors[i % colors.length]}
              rx="0.3"
            />
          ))}
        </>
      ) : (
        Array.from({ length: 16 }).map((_, i) => (
          <rect
            key={i}
            x={3 + (i % 4) * 6.5}
            y={2 + Math.floor(i / 4) * 4.5}
            width="5.5"
            height="3.5"
            fill={colors[i % colors.length]}
            rx="0.3"
          />
        ))
      )}
    </SvgWrap>
  );
}

function FlowIcon({ size, kind }: { size: number; kind: "sankey" | "themeRiver" }) {
  if (kind === "sankey") {
    return (
      <SvgWrap size={size}>
        <rect x="3" y="4" width="4" height="12" fill={ACCENT} rx="0.5" />
        <rect x="25" y="3" width="4" height="6" fill="#4FC3F7" rx="0.5" />
        <rect x="25" y="11" width="4" height="6" fill="#81C784" rx="0.5" />
        <path d="M 7 8 C 14 8 14 6 21 6" stroke={MUTED} strokeWidth="2" fill="none" opacity="0.7" />
        <path d="M 7 12 C 14 12 14 14 21 14" stroke={MUTED} strokeWidth="2" fill="none" opacity="0.7" />
      </SvgWrap>
    );
  }
  return (
    <SvgWrap size={size}>
      <path d="M 2 14 Q 8 6 14 12 T 26 8 T 30 14 L 30 18 L 2 18 Z" fill={`${ACCENT}55`} />
      <path d="M 2 16 Q 10 12 16 15 T 30 13" fill="none" stroke={ACCENT} strokeWidth="1.5" />
    </SvgWrap>
  );
}

function StatisticalIcon({ size, kind }: { size: number; kind: "boxplot" | "candlestick" }) {
  if (kind === "candlestick") {
    return (
      <SvgWrap size={size}>
        <line x1="8" y1="4" x2="8" y2="16" stroke={MUTED} strokeWidth="1" />
        <rect x="6" y="7" width="4" height="6" fill="#81C784" stroke="#2E7D32" strokeWidth="0.5" />
        <line x1="16" y1="5" x2="16" y2="15" stroke={MUTED} strokeWidth="1" />
        <rect x="14" y="6" width="4" height="7" fill="#E57373" stroke="#C62828" strokeWidth="0.5" />
        <line x1="24" y1="6" x2="24" y2="17" stroke={MUTED} strokeWidth="1" />
        <rect x="22" y="9" width="4" height="5" fill="#81C784" stroke="#2E7D32" strokeWidth="0.5" />
      </SvgWrap>
    );
  }
  return (
    <SvgWrap size={size}>
      <line x1="8" y1="4" x2="8" y2="16" stroke={MUTED} strokeWidth="1" />
      <rect x="5" y="8" width="6" height="4" fill={`${ACCENT}66`} stroke={ACCENT} strokeWidth="1" />
      <line x1="18" y1="5" x2="18" y2="15" stroke={MUTED} strokeWidth="1" />
      <rect x="15" y="7" width="6" height="5" fill={`${ACCENT}66`} stroke={ACCENT} strokeWidth="1" />
      <line x1="26" y1="6" x2="26" y2="16" stroke={MUTED} strokeWidth="1" />
      <rect x="23" y="9" width="6" height="3" fill={`${ACCENT}66`} stroke={ACCENT} strokeWidth="1" />
    </SvgWrap>
  );
}

function MixedIcon({ size }: { size: number }) {
  return (
    <SvgWrap size={size}>
      <rect x="4" y="10" width="4" height="8" fill={ACCENT} rx="0.5" />
      <rect x="11" y="6" width="4" height="12" fill={ACCENT} rx="0.5" />
      <rect x="18" y="8" width="4" height="10" fill={ACCENT} rx="0.5" />
      <polyline points="4,8 11,10 18,5 25,7" fill="none" stroke="#E91E63" strokeWidth="1.8" strokeLinecap="round" />
    </SvgWrap>
  );
}

function WaterfallIcon({ size }: { size: number }) {
  return (
    <SvgWrap size={size}>
      <rect x="4" y="8" width="5" height="9" fill={ACCENT} rx="0.5" />
      <rect x="12" y="5" width="5" height="6" fill="#4FC3F7" rx="0.5" />
      <rect x="20" y="10" width="5" height="7" fill="#81C784" rx="0.5" />
      <line x1="3" y1="17" x2="29" y2="17" stroke={MUTED} strokeWidth="0.8" />
    </SvgWrap>
  );
}

function PictorialBarIcon({ size }: { size: number }) {
  return (
    <SvgWrap size={size}>
      <rect x="5" y="11" width="5" height="7" fill={ACCENT} rx="2" />
      <rect x="13" y="7" width="5" height="11" fill={ACCENT} rx="2" />
      <rect x="21" y="4" width="5" height="14" fill={ACCENT} rx="2" />
    </SvgWrap>
  );
}

function ParallelIcon({ size }: { size: number }) {
  return (
    <SvgWrap size={size}>
      {Array.from({ length: 5 }).map((_, i) => (
        <line key={i} x1={5 + i * 5.5} y1="3" x2={5 + i * 5.5} y2="17" stroke={MUTED} strokeWidth="0.8" />
      ))}
      <polyline points="5,12 10,8 15,14 20,6 25,11" fill="none" stroke={ACCENT} strokeWidth="1.5" strokeLinecap="round" />
    </SvgWrap>
  );
}

export default function ChartTypeIcon({ typeId, size = 28 }: Props) {
  const id = normalizeChartTypeId(typeId);
  switch (id) {
    case "line":
      return <LineIcon size={size} />;
    case "area":
      return <LineIcon size={size} area />;
    case "stackedBar":
    case "bar":
      return <BarIcon size={size} />;
    case "stackedLine":
      return <LineIcon size={size} />;
    case "stackedArea":
      return <LineIcon size={size} area />;
    case "mixed":
      return <MixedIcon size={size} />;
    case "waterfall":
      return <WaterfallIcon size={size} />;
    case "pictorialBar":
      return <PictorialBarIcon size={size} />;
    case "scatter":
      return <ScatterIcon size={size} />;
    case "effectScatter":
      return <ScatterIcon size={size} effect />;
    case "pie":
      return <PieIcon size={size} />;
    case "doughnut":
      return <PieIcon size={size} doughnut />;
    case "rosePie":
      return <PieIcon size={size} rose />;
    case "funnel":
      return <FunnelIcon size={size} />;
    case "radar":
      return <RadarIcon size={size} />;
    case "gauge":
      return <GaugeIcon size={size} />;
    case "parallel":
      return <ParallelIcon size={size} />;
    case "sunburst":
      return <HierarchyIcon size={size} kind="sunburst" />;
    case "treemap":
      return <HierarchyIcon size={size} kind="treemap" />;
    case "tree":
      return <HierarchyIcon size={size} kind="tree" />;
    case "heatmap":
      return <HeatmapIcon size={size} />;
    case "calendarHeatmap":
      return <HeatmapIcon size={size} calendar />;
    case "themeRiver":
      return <FlowIcon size={size} kind="themeRiver" />;
    case "sankey":
      return <FlowIcon size={size} kind="sankey" />;
    case "boxplot":
      return <StatisticalIcon size={size} kind="boxplot" />;
    case "candlestick":
      return <StatisticalIcon size={size} kind="candlestick" />;
    default:
      return <BarIcon size={size} />;
  }
}
